import { useEffect, useState, type ReactNode } from 'react'
import Icon from '../Icon'
import { api, go, type Me } from '../../lib/classroom'
import { L, w } from '../../lib/words'
import { celebrate, sound } from '../../lib/fun'
import { CALL_BONUS, LEVELS, MILESTONE_XP, XP } from '../../../shared/classroom'
import Pichi, { stageFor } from './Pichi'

/** A bottom sheet: slides up, closes on the backdrop or Escape. */
export function Sheet({ open, onClose, children, title }: { open: boolean; onClose: () => void; children: ReactNode; title: string }) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="sheet-back" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <button className="sheet-x" aria-label={w('cancel')} onClick={onClose}><Icon name="close" /></button>
        {children}
      </div>
    </div>
  )
}

/** Tap the level card: the whole ladder, egg to adult, and how XP is earned. */
export function LevelSheet({ me, open, onClose }: { me: Me; open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title={w('levels')}>
      <h2>{w('levels')}</h2>
      <p className="muted small">{w('levelsSub')}</p>
      <ol className="ladder">
        {LEVELS.map((l, i) => {
          const here = i === me.level.index
          const done = i < me.level.index
          return (
            <li key={i} className={here ? 'here' : done ? 'done' : 'ahead'}>
              <Pichi size={64} stage={stageFor(i)} mood={here ? 'cheer' : 'happy'} look={here ? me.look : undefined} />
              <span className="ladder-name">
                <b>{w('level')} {i + 1} · {L(l.title)}</b>
                <span className="small">{w('from')} {l.min.toLocaleString()} {w('oro')}</span>
              </span>
              {here && <span className="level-chip">{w('youAreHere')}</span>}
              {done && <Icon name="check" />}
            </li>
          )
        })}
      </ol>
      <h3>{w('howToEarn')}</h3>
      <ul className="earn">
        <li><span>{w('earnRight')}</span><b>+{XP.right}</b></li>
        <li><span>{w('earnTry')}</span><b>+{XP.wrong}</b></li>
        <li><span>{w('earnIdk')}</span><b>+{XP.idk}</b></li>
        <li><span>{w('earnPractice')}</span><b>+{Math.floor(XP.right / 2)}</b></li>
        <li><span>{w('earnMilestone')}</span><b>+{MILESTONE_XP}</b></li>
        <li><span>{w('earnCall')}</span><b>+{CALL_BONUS}</b></li>
        <li><span>{w('earnBadge')}</span><b>+50 / 150 / 400</b></li>
      </ul>
    </Sheet>
  )
}

export function JoinSheet({ open, onClose, onJoined }: { open: boolean; onClose: () => void; onJoined: () => Promise<void> }) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const join = async () => {
    setError(null)
    try {
      await api.join(code)
      setCode('')
      celebrate()
      sound('level')
      await onJoined()
      onClose()
    } catch (e) {
      setError((e as Error).message)
    }
  }
  return (
    <Sheet open={open} onClose={onClose} title={w('joinAClass')}>
      <div className="center"><Pichi size={90} stage={2} mood="wave" /></div>
      <h2 className="center">{w('joinAClass')}</h2>
      <CodeInput value={code} onChange={setCode} onEnter={join} />
      <p className="muted small center">{w('codeHint')}</p>
      <button className="primary big" disabled={code.length < 3} onClick={join}>{w('join')} <Icon name="chevron" /></button>
      {error && <p className="error">{error}</p>}
    </Sheet>
  )
}

/** The class code box: big, centred, one letter and two numbers. */
export function CodeInput({ value, onChange, onEnter }: { value: string; onChange: (v: string) => void; onEnter?: () => void }) {
  return (
    <input
      className="code-input"
      value={value}
      onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
      onKeyDown={(e) => { if (e.key === 'Enter' && value.length >= 3) onEnter?.() }}
      placeholder="K47"
      autoCapitalize="characters"
      autoCorrect="off"
      spellCheck={false}
      aria-label={w('classCode')}
    />
  )
}

export function FeedbackSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [text, setText] = useState('')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const send = async () => {
    setError(null)
    try {
      await api.feedback(text)
      setSent(true)
      setText('')
      celebrate()
      setTimeout(() => { setSent(false); onClose() }, 1600)
    } catch (e) {
      setError((e as Error).message)
    }
  }
  return (
    <Sheet open={open} onClose={onClose} title={w('feedback')}>
      <div className="center"><Pichi size={90} stage={2} mood={sent ? 'cheer' : 'think'} /></div>
      <h2 className="center">{sent ? w('feedbackThanks') : w('feedbackTitle')}</h2>
      {!sent && (
        <>
          <p className="muted small center">{w('feedbackSub')}</p>
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} />
          <button className="primary big" disabled={!text.trim()} onClick={send}>{w('send')} <Icon name="chevron" /></button>
          {error && <p className="error">{error}</p>}
        </>
      )}
    </Sheet>
  )
}

/** Pinned to the bottom of the phone: join a class, and tell us what you think. */
export function BottomBar({ student, onJoined }: { student: boolean; onJoined: () => Promise<void> }) {
  const [joining, setJoining] = useState(false)
  const [talking, setTalking] = useState(false)
  return (
    <>
      <div className="bottom-bar">
        {student && <button className="go" onClick={() => setJoining(true)}><Icon name="people" /> {w('joinAClass')}</button>}
        {student && <button onClick={() => go('/tienda')} aria-label={w('shop')}><Icon name="shop" /></button>}
        <button onClick={() => setTalking(true)} aria-label={w('feedback')}><Icon name="bulb" /> {!student && w('feedback')}</button>
      </div>
      <JoinSheet open={joining} onClose={() => setJoining(false)} onJoined={onJoined} />
      <FeedbackSheet open={talking} onClose={() => setTalking(false)} />
    </>
  )
}
